import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Sparkles, Users, ArrowRight, Check, TrendingDown } from "lucide-react";

/**
 * Founding-Member card for the Billing dashboard.
 * Two variants:
 *   - NEW SUBSCRIBER  → no active sub → full-featured checkout card
 *   - MONTHLY→ANNUAL  → Growth monthly active sub → compact one-click switch card
 * Hides if: sold out, already a founder, or user is on Growth annual / other paid tier.
 */
export default function FoundingCard({ user, sub }) {
  const [seats, setSeats] = useState(null);
  const [meStatus, setMeStatus] = useState(null);
  const [busy, setBusy] = useState(false);
  const [confirmingSwitch, setConfirmingSwitch] = useState(false);

  const reload = () => {
    api.get("/pricing/founding-seats").then((r) => setSeats(r.data)).catch(() => {});
    api.get("/founding/me").then((r) => setMeStatus(r.data)).catch(() => {});
  };
  useEffect(() => { reload(); }, []);

  if (!seats || seats.remaining === 0) return null;
  if (meStatus?.is_founder) return null;

  // Determine which variant to render.
  const isActive = sub?.subscription_status === "active";
  const canSwitch = meStatus?.can_switch_from_monthly;
  if (isActive && !canSwitch) return null;   // Growth annual / other paid → hide
  const variant = canSwitch ? "switch" : "new";

  const claim = async () => {
    setBusy(true);
    try {
      const r = await api.post("/founding/checkout");
      if (r.data?.url) window.location.href = r.data.url;
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not start checkout. Please try again.");
      setBusy(false);
    }
  };

  const switchToFounding = async () => {
    setBusy(true);
    try {
      await api.post("/founding/switch");
      toast.success("You're now a Founding Member! Your seat number is being assigned.");
      setConfirmingSwitch(false);
      // Give the webhook a beat to run, then refresh so the card hides and the badge appears.
      setTimeout(() => window.location.reload(), 1500);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not switch. Please try again or contact support.");
      setBusy(false);
      setConfirmingSwitch(false);
    }
  };

  const pct = Math.max(4, Math.round((seats.taken / seats.total) * 100)); // internal — used by admin, hidden from users

  // -------- Variant A: Monthly → Annual switch (compact) --------
  if (variant === "switch") {
    return (
      <div
        data-testid="founding-card-switch"
        className="relative overflow-hidden rounded-2xl border-2 border-lime-400 bg-gradient-to-br from-emerald-950 via-emerald-900 to-emerald-950 p-6 text-white shadow-xl mb-8"
      >
        <div className="absolute -top-10 -right-10 h-40 w-40 rounded-full bg-lime-400/10 blur-3xl" />
        <div className="relative grid md:grid-cols-[1fr_auto] gap-6 items-center">
          <div>
            <div className="inline-flex items-center gap-1.5 text-[11px] font-bold uppercase tracking-[0.2em] text-lime-300">
              <TrendingDown className="h-3.5 w-3.5" /> Growth monthly → Founding · exclusive offer
            </div>
            <h3 className="mt-2 font-heading text-xl md:text-2xl font-bold tracking-tight">
              Lock <span className="text-lime-400">$39/mo forever</span> — save <span className="text-lime-400">$132/year</span>, every year.
            </h3>
            <p className="mt-1.5 text-sm text-emerald-100/80">
              You&apos;re on Growth monthly at $50. Switch to annual Founding pricing and lock in a lifetime discount before the 100 seats fill up.
            </p>
            <ul className="mt-4 grid sm:grid-cols-2 gap-x-4 gap-y-1.5 text-sm text-emerald-50/90">
              <li className="flex items-start gap-2"><Check className="h-4 w-4 mt-0.5 shrink-0 text-lime-400" /> $39/mo effective forever</li>
              <li className="flex items-start gap-2"><Check className="h-4 w-4 mt-0.5 shrink-0 text-lime-400" /> Founder badge #001–#100</li>
              <li className="flex items-start gap-2"><Check className="h-4 w-4 mt-0.5 shrink-0 text-lime-400" /> Refer 3 → $29/mo forever</li>
              <li className="flex items-start gap-2"><Check className="h-4 w-4 mt-0.5 shrink-0 text-lime-400" /> Direct line to the founder</li>
            </ul>
          </div>
          <div className="text-center md:min-w-[200px] flex flex-col justify-center">
            <button
              onClick={() => setConfirmingSwitch(true)}
              disabled={busy}
              data-testid="founding-switch-cta"
              className="w-full inline-flex items-center justify-center gap-2 bg-lime-400 text-emerald-950 rounded-lg px-4 py-3 text-sm font-semibold hover:bg-lime-300 disabled:opacity-60 transition-all hover:-translate-y-0.5"
            >
              Switch to Founding <ArrowRight className="h-4 w-4" />
            </button>
            <p className="text-[10px] text-emerald-100/60 mt-2">Prorated · billed today</p>
          </div>
        </div>

        {confirmingSwitch && (
          <div className="mt-5 rounded-lg bg-emerald-900/60 border border-lime-400/40 p-4" data-testid="founding-switch-confirm">
            <p className="text-sm text-white font-semibold mb-1">Confirm switch to Founding annual</p>
            <p className="text-xs text-emerald-100/80 mb-3">
              Stripe will invoice you today for <b className="text-white">$468</b> minus a proration credit for the unused portion of your current month. Your next charge after that is <b className="text-white">1 year</b> from today at the same $468 rate — locked forever.
            </p>
            <div className="flex gap-2 flex-wrap">
              <button
                onClick={switchToFounding}
                disabled={busy}
                data-testid="founding-switch-confirm-yes"
                className="text-xs font-semibold bg-lime-400 text-emerald-950 rounded-md px-4 py-2 hover:bg-lime-300 disabled:opacity-60"
              >
                {busy ? "Switching…" : "Yes, switch me — charge me now"}
              </button>
              <button
                onClick={() => setConfirmingSwitch(false)}
                disabled={busy}
                data-testid="founding-switch-confirm-no"
                className="text-xs font-semibold bg-transparent border border-emerald-700 text-emerald-100 rounded-md px-4 py-2 hover:bg-emerald-800"
              >
                Never mind
              </button>
            </div>
          </div>
        )}
      </div>
    );
  }

  // -------- Variant B: New subscriber (existing full card) --------
  const perks = [
    "$39/mo effective price — locked as long as your sub stays active",
    "Founder badge with your number (#001–#100) on your profile",
    "Refer 3 paid subs → price drops to $29/mo forever",
    "Direct line to the founder for feature requests",
    "First look at every new Pro/Enterprise feature",
  ];
  return (
    <div
      data-testid="founding-card"
      className="relative overflow-hidden rounded-2xl border-2 border-lime-400 bg-gradient-to-br from-emerald-950 via-emerald-900 to-emerald-950 p-6 md:p-8 text-white shadow-xl mb-8"
    >
      <div className="absolute -top-10 -right-10 h-40 w-40 rounded-full bg-lime-400/10 blur-3xl" />
      <div className="absolute -bottom-8 -left-8 h-32 w-32 rounded-full bg-lime-400/10 blur-2xl" />
      <div className="relative grid md:grid-cols-[1fr_auto] gap-6 items-center">
        <div>
          <div className="inline-flex items-center gap-1.5 text-[11px] font-bold uppercase tracking-[0.2em] text-lime-300">
            <Sparkles className="h-3.5 w-3.5" /> BidMatchAI Founding 100 · Limited-time
          </div>
          <h3 className="mt-2 font-heading text-2xl md:text-3xl font-bold tracking-tight">
            <span className="line-through text-emerald-100/40 text-xl mr-2">$50</span>
            <span className="text-lime-400">$100</span>
            <span className="text-emerald-100/80 text-lg font-normal">/mo · billed annually</span>
          </h3>
          <p className="mt-1 text-sm text-emerald-100/80">One payment of <b className="text-white">$468</b> locks the discount for life. Save <b className="text-lime-300">$132/year</b>, every year, as long as you stay.</p>
          <ul className="mt-5 grid gap-2">
            {perks.map((p) => (
              <li key={p} className="flex items-start gap-2 text-sm text-emerald-50/90">
                <Check className="h-4 w-4 mt-0.5 shrink-0 text-lime-400" />
                <span>{p}</span>
              </li>
            ))}
          </ul>
        </div>
        <div className="text-center md:min-w-[220px] flex flex-col justify-center">
          <button
            onClick={claim}
            disabled={busy}
            data-testid="founding-card-cta"
            className="w-full inline-flex items-center justify-center gap-2 bg-lime-400 text-emerald-950 rounded-lg px-4 py-3 text-sm font-semibold hover:bg-lime-300 disabled:opacity-60 transition-all hover:-translate-y-0.5"
          >
            {busy ? "Opening checkout…" : "Claim my Founder seat"}
            {!busy && <ArrowRight className="h-4 w-4" />}
          </button>
          <p className="text-[10px] text-emerald-100/50 mt-2">No trial · Charged today · Cancel anytime</p>
        </div>
      </div>
    </div>
  );
}
